/**
 * Settings screen - thresholds, calibration, crew metadata
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  Pressable,
  ScrollView,
  useColorScheme,
  Platform,
  Switch,
  Alert,
  Appearance,
  Linking,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useExposureStore } from '../store';
import { storage } from '../utils/storage';
import {
  isHealthAvailable,
  requestHealthAuthorization,
  getHealthKitDiagnostic,
} from '../services/healthAppService';

const HEALTH_SYNC_KEY = 'health_sync_enabled';
const COLOR_SCHEME_KEY = 'color_scheme_override';
import {
  computeCalibrationFromKnownDose,
  CALIBRATION_FACTOR_MICRO_SV_PER_HIT,
} from '../services/cameraRadiationService';
import type { CrewRole } from '../types';

const ACCENT_CYAN = '#00d4ff';
const ACCENT_CYAN_DIM = '#00a3cc';
const CARD_BG = '#1e293b';
const CARD_BORDER = '#334155';

const CREW_ROLES: { value: CrewRole | ''; label: string }[] = [
  { value: '', label: 'Not set' },
  { value: 'pilot', label: 'Pilot' },
  { value: 'first_officer', label: 'First Officer' },
  { value: 'flight_attendant', label: 'Flight Attendant' },
  { value: 'purser', label: 'Purser' },
  { value: 'other', label: 'Other' },
];

export function SettingsScreen() {
  const isDark = useColorScheme() === 'dark';
  const [knownDose, setKnownDose] = useState('');
  const [observedHits, setObservedHits] = useState('');
  const [calAltitude, setCalAltitude] = useState('35000');

  const calibrationFactor = useExposureStore(
    (s) => s.calibrationFactorMicroSvPerHit
  );
  const setCalibrationFactor = useExposureStore(
    (s) => s.setCalibrationFactorMicroSvPerHit
  );
  const defaultFlightId = useExposureStore((s) => s.defaultFlightId);
  const setDefaultFlightId = useExposureStore((s) => s.setDefaultFlightId);
  const defaultCrewRole = useExposureStore((s) => s.defaultCrewRole);
  const setDefaultCrewRole = useExposureStore((s) => s.setDefaultCrewRole);
  const healthSyncEnabled = useExposureStore((s) => s.healthSyncEnabled);
  const setHealthSyncEnabled = useExposureStore((s) => s.setHealthSyncEnabled);
  const [healthSyncRequesting, setHealthSyncRequesting] = useState(false);
  const simulateHighEnergyHits = useExposureStore((s) => s.simulateHighEnergyHits);
  const setSimulateHighEnergyHits = useExposureStore(
    (s) => s.setSimulateHighEnergyHits
  );
  const colorSchemeOverride = useExposureStore((s) => s.colorSchemeOverride);
  const setColorSchemeOverride = useExposureStore(
    (s) => s.setColorSchemeOverride
  );

  useEffect(() => {
    if (Platform.OS === 'web') return;
    storage.getItem(HEALTH_SYNC_KEY).then((v) => {
      if (v === 'true') setHealthSyncEnabled(true);
    });
  }, [setHealthSyncEnabled]);

  useEffect(() => {
    storage.getItem(COLOR_SCHEME_KEY).then((v) => {
      if (v === 'dark' || v === 'light') {
        setColorSchemeOverride(v);
        if (Platform.OS !== 'web') Appearance.setColorScheme(v);
      }
    });
  }, [setColorSchemeOverride]);

  const handleColorSchemeChange = (scheme: 'dark' | 'light' | null) => {
    setColorSchemeOverride(scheme);
    storage.setItem(COLOR_SCHEME_KEY, scheme ?? '');
    if (Platform.OS !== 'web' && scheme) {
      Appearance.setColorScheme(scheme);
    } else if (Platform.OS !== 'web') {
      Appearance.setColorScheme(Appearance.getColorScheme() ?? 'light');
    }
  };

  const handleHealthSyncToggle = async (value: boolean) => {
    if (value) {
      setHealthSyncRequesting(true);
      setHealthSyncEnabled(true); // Optimistic: show switch ON immediately so tap feels responsive
      try {
        const status = await requestHealthAuthorization();
        if (status === 'authorized') {
          storage.setItem(HEALTH_SYNC_KEY, 'true');
        } else {
          setHealthSyncEnabled(false); // Revert on failure
          const diag = status === 'unknown' ? `\n\nDebug: ${getHealthKitDiagnostic()}` : '';
          const msg =
            status === 'not_authorized'
              ? 'Permission denied. Open Settings to enable FVrad in Health > Data Access.'
              : status === 'unknown'
                ? `Could not connect to Health. Rebuild the app and try again.${diag}`
                : 'Health app requires a physical iPhone. It does not work in the Simulator.';
          Alert.alert(
            'Health App',
            msg,
            status === 'not_authorized'
              ? [
                  { text: 'Cancel', style: 'cancel' },
                  { text: 'Open Settings', onPress: () => Linking.openSettings() },
                ]
              : [{ text: 'OK' }]
          );
        }
      } catch (err) {
        setHealthSyncEnabled(false);
        Alert.alert(
          'Health App',
          `Unexpected error: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        setHealthSyncRequesting(false);
      }
    } else {
      setHealthSyncEnabled(false);
      storage.setItem(HEALTH_SYNC_KEY, 'false');
    }
  };

  const theme = isDark
    ? { card: CARD_BG, border: CARD_BORDER, text: '#f1f5f9', muted: '#94a3b8' }
    : { card: '#f8fafc', border: '#e2e8f0', text: '#0f172a', muted: '#64748b' };

  const containerBg = isDark ? '#0f172a' : '#f8fafc';

  const handleCalibrate = () => {
    const dose = parseFloat(knownDose);
    const hits = parseFloat(observedHits);
    const alt = parseInt(calAltitude, 10) || 35000;
    if (dose > 0 && hits > 0) {
      const factor = computeCalibrationFromKnownDose(dose, hits, alt);
      setCalibrationFactor(factor);
    }
  };

  const handleResetCalibration = () => {
    setCalibrationFactor(CALIBRATION_FACTOR_MICRO_SV_PER_HIT);
    setKnownDose('');
    setObservedHits('');
    setCalAltitude('35000');
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: containerBg }]}
      edges={['top']}
      testID="settings-screen"
    >
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Text style={[styles.title, { color: ACCENT_CYAN }]} testID="settings-title">Settings</Text>

        {/* Dark / Light mode */}
        <View
          style={[
            styles.card,
            { backgroundColor: theme.card, borderColor: theme.border },
          ]}
        >
          <Text style={[styles.cardLabel, { color: theme.muted }]}>
            Appearance
          </Text>
          <View style={styles.themeRow}>
            {(['light', 'dark', null] as const).map((scheme) => (
              <Pressable
                key={scheme ?? 'system'}
                style={[
                  styles.themeChip,
                  {
                    borderColor: theme.border,
                    backgroundColor:
                      colorSchemeOverride === scheme ? ACCENT_CYAN_DIM : theme.card,
                  },
                ]}
                onPress={() => handleColorSchemeChange(scheme)}
              >
                <Text
                  style={[
                    styles.themeChipText,
                    {
                      color:
                        colorSchemeOverride === scheme ? '#0a0e17' : theme.muted,
                    },
                  ]}
                >
                  {scheme === 'light' ? '☀ Light' : scheme === 'dark' ? '🌙 Dark' : 'System'}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        {/* Calibration for Flight Altitude */}
        <View
          style={[
            styles.card,
            { backgroundColor: theme.card, borderColor: theme.border },
          ]}
        >
          <Text style={[styles.cardLabel, { color: theme.muted }]}>
            Calibrate for Flight Altitude
          </Text>
          <Text style={[styles.cardUnit, { color: theme.muted }]}>
            Enter known cosmic dose (µSv/h) and observed hits/sec to adjust
            calibration factor
          </Text>
          <TextInput
            style={[styles.input, { color: theme.text, borderColor: theme.border }]}
            placeholder="Known dose (µSv/h)"
            placeholderTextColor={theme.muted}
            value={knownDose}
            onChangeText={setKnownDose}
            keyboardType="decimal-pad"
          />
          <TextInput
            style={[styles.input, { color: theme.text, borderColor: theme.border }]}
            placeholder="Observed hits/sec"
            placeholderTextColor={theme.muted}
            value={observedHits}
            onChangeText={setObservedHits}
            keyboardType="decimal-pad"
          />
          <TextInput
            style={[styles.input, { color: theme.text, borderColor: theme.border }]}
            placeholder="Altitude (ft)"
            placeholderTextColor={theme.muted}
            value={calAltitude}
            onChangeText={setCalAltitude}
            keyboardType="numeric"
          />
          <View style={styles.calRow}>
            <Text style={[styles.cardUnit, { color: theme.muted }]}>
              Current factor: {calibrationFactor.toFixed(2)} µSv/h per hit
            </Text>
          </View>
          <View style={styles.buttonRow}>
            <Pressable
              style={[styles.primaryButton, !isDark && styles.primaryButtonLight]}
              onPress={handleCalibrate}
            >
              <Text style={styles.primaryButtonText}>Apply calibration</Text>
            </Pressable>
            <Pressable
              style={[styles.secondaryButton, { borderColor: theme.border }]}
              onPress={handleResetCalibration}
            >
              <Text style={[styles.secondaryButtonText, { color: theme.muted }]}>
                Reset
              </Text>
            </Pressable>
          </View>
        </View>

        {/* Crew metadata for reports */}
        <View
          style={[
            styles.card,
            { backgroundColor: theme.card, borderColor: theme.border },
          ]}
        >
          <Text style={[styles.cardLabel, { color: theme.muted }]}>
            Crew metadata (for exportable reports)
          </Text>
          <TextInput
            style={[styles.input, { color: theme.text, borderColor: theme.border }]}
            placeholder="Default Flight ID"
            placeholderTextColor={theme.muted}
            value={defaultFlightId}
            onChangeText={setDefaultFlightId}
          />
          <View style={styles.roleRow}>
            <Text style={[styles.cardUnit, { color: theme.muted }]}>
              Crew role:
            </Text>
            {CREW_ROLES.map((r) => (
              <Pressable
                key={r.value || 'none'}
                style={[
                  styles.roleChip,
                  {
                    borderColor: theme.border,
                    backgroundColor:
                      defaultCrewRole === r.value ? ACCENT_CYAN_DIM : theme.card,
                  },
                ]}
                onPress={() => setDefaultCrewRole(r.value)}
              >
                <Text
                  style={[
                    styles.roleChipText,
                    {
                      color:
                        defaultCrewRole === r.value ? '#0a0e17' : theme.muted,
                    },
                  ]}
                >
                  {r.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        {/* Health app sync */}
        {isHealthAvailable() && (
          <View
            style={[
              styles.card,
              { backgroundColor: theme.card, borderColor: theme.border },
            ]}
          >
            <Text style={[styles.cardLabel, { color: theme.muted }]}>
              Health app tracker
            </Text>
            <Text style={[styles.cardUnit, { color: theme.muted }]}>
              {Platform.OS === 'ios'
                ? 'Sync flights to Apple Health as workouts'
                : 'Sync flights to Health Connect as workouts'}
            </Text>
            <View style={styles.toggleRow}>
              <Text style={[styles.cardUnit, { color: theme.text }]}>
                Enable sync
              </Text>
              <Switch
                value={healthSyncEnabled}
                onValueChange={handleHealthSyncToggle}
                disabled={healthSyncRequesting}
                trackColor={{ false: theme.border, true: ACCENT_CYAN_DIM }}
                thumbColor={healthSyncEnabled ? ACCENT_CYAN : '#94a3b8'}
              />
            </View>
          </View>
        )}

        {/* Test filter - simulate high-energy hits (no source needed) */}
        {Platform.OS !== 'web' && (
          <View
            style={[
              styles.card,
              { backgroundColor: theme.card, borderColor: theme.border },
            ]}
          >
            <Text style={[styles.cardLabel, { color: theme.muted }]}>
              Test high-energy filter
            </Text>
            <Text style={[styles.cardUnit, { color: theme.muted }]}>
              Simulate cosmic-ray-like events to verify the filter works. Use with
              Live Monitoring on. No radiation source needed.
            </Text>
            <View style={styles.toggleRow}>
              <Text style={[styles.cardUnit, { color: theme.text }]}>
                Simulate hits
              </Text>
              <Switch
                value={simulateHighEnergyHits}
                onValueChange={setSimulateHighEnergyHits}
                trackColor={{ false: theme.border, true: ACCENT_CYAN_DIM }}
                thumbColor={simulateHighEnergyHits ? ACCENT_CYAN : '#94a3b8'}
              />
            </View>
          </View>
        )}

        {/* Thresholds */}
        <View
          style={[
            styles.card,
            { backgroundColor: theme.card, borderColor: theme.border },
          ]}
        >
          <Text style={[styles.cardLabel, { color: theme.muted }]}>
            Alert thresholds
          </Text>
          <Text style={[styles.cardUnit, { color: theme.muted }]}>
            Configure in future update
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { padding: 20, paddingBottom: 40 },
  title: { fontSize: 24, fontWeight: '700', marginBottom: 20 },
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
  },
  cardLabel: { fontSize: 14, fontWeight: '600', marginBottom: 4 },
  cardUnit: { fontSize: 12, marginTop: 4, marginBottom: 8 },
  input: {
    borderWidth: 1,
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 16,
  },
  calRow: { marginBottom: 12 },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  buttonRow: { flexDirection: 'row', gap: 12, marginTop: 8 },
  primaryButton: {
    flex: 1,
    backgroundColor: ACCENT_CYAN,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  primaryButtonLight: { backgroundColor: ACCENT_CYAN_DIM },
  primaryButtonText: { color: '#0a0e17', fontWeight: '700', fontSize: 16 },
  secondaryButton: {
    flex: 1,
    borderWidth: 2,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  secondaryButtonText: { fontWeight: '600' },
  roleRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  roleChip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  roleChipText: { fontSize: 13, fontWeight: '500' },
  themeRow: { flexDirection: 'row', gap: 8, marginTop: 8 },
  themeChip: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  themeChipText: { fontSize: 13, fontWeight: '600' },
});
